import {
  themePreferenceDecision,
  themeSelectionName,
} from "./theme-preference-repository.js";
import {
  browserThemePreferenceRepository,
} from "./appearance-preferences.js";

const THEME_EVENT = "fishystuff:themechange";
const THEME_PROBE_ID = "fishystuff-theme-probe";
const SYSTEM_QUERY = "(prefers-color-scheme: dark)";
const SYSTEM_SELECTION = Object.freeze({ kind: "system" });

function ensureProbe(doc) {
  if (!doc?.body) return null;
  let probe = doc.getElementById(THEME_PROBE_ID);
  if (probe) return probe;

  probe = doc.createElement("div");
  probe.id = THEME_PROBE_ID;
  probe.setAttribute("aria-hidden", "true");
  probe.style.position = "fixed";
  probe.style.width = "0";
  probe.style.height = "0";
  probe.style.overflow = "hidden";
  probe.style.opacity = "0";
  probe.style.pointerEvents = "none";
  probe.innerHTML = [
    '<div data-role="base" class="bg-base-100 text-base-content"></div>',
    '<div data-role="surface" class="bg-base-200 border border-base-300"></div>',
    '<div data-role="primary" class="bg-primary text-primary-content"></div>',
    '<div data-role="secondary" class="bg-secondary text-secondary-content"></div>',
    '<div data-role="accent" class="bg-accent text-accent-content"></div>',
    '<div data-role="neutral" class="bg-neutral text-neutral-content"></div>',
    '<div data-role="info" class="bg-info text-info-content"></div>',
    '<div data-role="success" class="bg-success text-success-content"></div>',
    '<div data-role="warning" class="bg-warning text-warning-content"></div>',
    '<div data-role="error" class="bg-error text-error-content"></div>',
  ].join("");
  doc.body.appendChild(probe);
  return probe;
}

function readColor(win, probe, selector, property) {
  const node = probe?.querySelector?.(selector);
  if (!node || typeof win?.getComputedStyle !== "function") return "";
  return String(win.getComputedStyle(node).getPropertyValue(property) || "").trim();
}

function resolveTheme(selection, mediaQuery) {
  if (selection.kind === "named") return selection.name;
  if (!mediaQuery) return "fishy";
  return mediaQuery.matches ? "fishy" : "light";
}

function setText(element, value) {
  if (element && element.textContent !== value) element.textContent = value;
}

function setAttribute(element, name, value) {
  if (element && element.getAttribute(name) !== value) {
    element.setAttribute(name, value);
  }
}

function toggleClass(element, name, enabled) {
  if (element?.classList?.contains(name) !== enabled) {
    element?.classList?.toggle(name, enabled);
  }
}

export function createThemePresentationController({
  repository,
  doc = globalThis.document,
  win = globalThis.window,
  matchMedia = win?.matchMedia?.bind(win),
  requestAnimationFrame = win?.requestAnimationFrame?.bind(win),
  cancelAnimationFrame = win?.cancelAnimationFrame?.bind(win),
} = {}) {
  if (
    !repository
    || typeof repository.decision !== "function"
    || typeof repository.selectNamedTheme !== "function"
    || typeof repository.followSystemTheme !== "function"
    || typeof repository.state !== "function"
    || typeof repository.subscribe !== "function"
  ) {
    throw new TypeError("Theme presentation requires the typed repository contract.");
  }
  if (!doc?.documentElement || !win) {
    throw new TypeError("Theme presentation requires a browser document and window.");
  }

  let started = false;
  let disposed = false;
  let unsubscribe = null;
  let mediaQuery = null;
  let pendingFrame = null;
  let publishGeneration = 0;
  let acknowledgedSelection = null;
  let acknowledgedKind = "uninitialized";
  let acknowledgedRevision = null;

  function snapshotTheme() {
    const probe = ensureProbe(doc);
    const selection = acknowledgedSelection || SYSTEM_SELECTION;
    return Object.freeze({
      theme: themeSelectionName(selection),
      resolvedTheme: doc.documentElement.getAttribute("data-theme") || "",
      repositoryKind: acknowledgedKind,
      revision: acknowledgedRevision,
      colors: Object.freeze({
        base100: readColor(win, probe, '[data-role="base"]', "background-color"),
        baseContent: readColor(win, probe, '[data-role="base"]', "color"),
        base200: readColor(win, probe, '[data-role="surface"]', "background-color"),
        base300: readColor(win, probe, '[data-role="surface"]', "border-top-color"),
        primary: readColor(win, probe, '[data-role="primary"]', "background-color"),
        primaryContent: readColor(win, probe, '[data-role="primary"]', "color"),
        secondary: readColor(win, probe, '[data-role="secondary"]', "background-color"),
        secondaryContent: readColor(win, probe, '[data-role="secondary"]', "color"),
        accent: readColor(win, probe, '[data-role="accent"]', "background-color"),
        accentContent: readColor(win, probe, '[data-role="accent"]', "color"),
        neutral: readColor(win, probe, '[data-role="neutral"]', "background-color"),
        neutralContent: readColor(win, probe, '[data-role="neutral"]', "color"),
        info: readColor(win, probe, '[data-role="info"]', "background-color"),
        infoContent: readColor(win, probe, '[data-role="info"]', "color"),
        success: readColor(win, probe, '[data-role="success"]', "background-color"),
        successContent: readColor(win, probe, '[data-role="success"]', "color"),
        warning: readColor(win, probe, '[data-role="warning"]', "background-color"),
        warningContent: readColor(win, probe, '[data-role="warning"]', "color"),
        error: readColor(win, probe, '[data-role="error"]', "background-color"),
        errorContent: readColor(win, probe, '[data-role="error"]', "color"),
      }),
    });
  }

  function publishSnapshot(dispatch) {
    if (disposed) return;
    const detail = snapshotTheme();
    win.__fishystuffTheme = detail;
    if (dispatch) {
      win.dispatchEvent(new win.CustomEvent(THEME_EVENT, { detail }));
    }
  }

  function queueSnapshot(dispatch) {
    publishGeneration += 1;
    const generation = publishGeneration;
    if (pendingFrame !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(pendingFrame);
    }
    pendingFrame = null;
    const publish = () => {
      if (generation !== publishGeneration) return;
      pendingFrame = null;
      publishSnapshot(dispatch);
    };
    if (typeof requestAnimationFrame === "function") {
      pendingFrame = requestAnimationFrame(publish);
    } else {
      queueMicrotask(publish);
    }
  }

  function syncCurrentPreview(resolvedName) {
    doc.querySelectorAll("[data-theme-current-label]").forEach((element) => {
      setText(element, resolvedName);
    });
    doc.querySelectorAll("[data-theme-current-preview]").forEach((element) => {
      setAttribute(element, "data-theme", resolvedName);
    });
  }

  function syncThemePanels(selectedName, canChange) {
    doc.querySelectorAll("[data-theme-panel]").forEach((panel) => {
      panel.setAttribute("data-theme-preference-state", acknowledgedKind);
      panel.querySelectorAll(".theme-item").forEach((element) => {
        const selected = element.getAttribute("data-theme-name") === selectedName;
        setAttribute(element, "aria-checked", String(selected));
        setAttribute(element, "aria-disabled", String(!canChange));
        if ("disabled" in element) element.disabled = !canChange;
        toggleClass(element, "bg-base-200", selected);
        toggleClass(element, "border-base-content", selected);
        toggleClass(element, "shadow-sm", selected);
      });
    });
  }

  function applySelection(selection, { canChange, dispatch }) {
    const selectedName = themeSelectionName(selection);
    const resolvedName = resolveTheme(selection, mediaQuery);
    setAttribute(doc.documentElement, "data-theme", resolvedName);
    setAttribute(
      doc.documentElement,
      "data-theme-preference-state",
      acknowledgedKind,
    );
    syncCurrentPreview(resolvedName);
    syncThemePanels(selectedName, canChange);
    queueSnapshot(dispatch);
  }

  function projectState(state) {
    const decision = themePreferenceDecision(state);
    const acknowledged = ["ready", "missing", "conflict"].includes(
      decision.repositoryKind,
    );
    if (acknowledged) {
      setAttribute(
        doc.documentElement,
        "data-theme-boot-cache-state",
        state?.bootCache?.kind === "unavailable" ? "unavailable" : "ready",
      );
      setAttribute(
        doc.documentElement,
        "data-theme-legacy-cleanup-state",
        state?.legacyCleanup?.kind === "unavailable" ? "unavailable" : "ready",
      );
      acknowledgedSelection = decision.selection;
      acknowledgedKind = decision.repositoryKind;
      acknowledgedRevision = decision.revision;
      applySelection(decision.selection, {
        canChange: decision.canChange,
        dispatch: true,
      });
      return;
    }

    if (!acknowledgedSelection) {
      acknowledgedKind = decision.repositoryKind;
      acknowledgedRevision = null;
      syncThemePanels(themeSelectionName(SYSTEM_SELECTION), false);
      setAttribute(
        doc.documentElement,
        "data-theme-preference-state",
        acknowledgedKind,
      );
      return;
    }
    acknowledgedKind = decision.repositoryKind;
    syncThemePanels(themeSelectionName(acknowledgedSelection), false);
    setAttribute(
      doc.documentElement,
      "data-theme-preference-state",
      acknowledgedKind,
    );
  }

  function commandOptions() {
    const state = repository.state();
    if (state.kind === "ready") return { expectedRevision: state.revision };
    if (state.kind === "missing") return { expectedRevision: null };
    return {};
  }

  function reportCommandFailure(state) {
    if (state?.kind === "ready") {
      if (state.bootCache?.kind === "unavailable") {
        win.__fishystuffToast?.error?.(
          "Theme was saved, but its early-load cache could not be updated.",
        );
      }
      if (state.legacyCleanup?.kind === "unavailable") {
        win.__fishystuffToast?.error?.(
          "Theme was saved, but retired theme data could not be removed.",
        );
      }
      return;
    }
    const message = state?.kind === "conflict"
      ? "Theme changed in another tab. The latest saved theme is now shown."
      : "Theme could not be saved. The previous acknowledged theme remains active.";
    win.__fishystuffToast?.error?.(message);
  }

  async function handleClick(event) {
    const item = event.target?.closest?.("[data-theme-panel] .theme-item");
    if (!item || item.getAttribute("aria-disabled") === "true") return;
    const name = item.getAttribute("data-theme-name");
    if (!name) return;
    try {
      const next = name === "system"
        ? await repository.followSystemTheme(commandOptions())
        : await repository.selectNamedTheme(name, commandOptions());
      reportCommandFailure(next);
    } catch (error) {
      globalThis.reportError?.(error);
      reportCommandFailure({ kind: "unavailable" });
    }
  }

  function handleSystemPreferenceChange() {
    if (!acknowledgedSelection || acknowledgedSelection.kind !== "system") return;
    applySelection(acknowledgedSelection, {
      canChange: repository.decision().canChange,
      dispatch: true,
    });
  }

  function handlePageHide(event) {
    if (event?.persisted !== true) dispose();
  }

  function start() {
    if (disposed || started) return;
    started = true;
    try {
      mediaQuery = typeof matchMedia === "function" ? matchMedia(SYSTEM_QUERY) : null;
    } catch (_error) {
      mediaQuery = null;
    }
    mediaQuery?.addEventListener?.("change", handleSystemPreferenceChange);
    doc.addEventListener("click", handleClick);
    win.addEventListener("pagehide", handlePageHide);
    unsubscribe = repository.subscribe(projectState);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    publishGeneration += 1;
    if (pendingFrame !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(pendingFrame);
    }
    pendingFrame = null;
    unsubscribe?.();
    unsubscribe = null;
    mediaQuery?.removeEventListener?.("change", handleSystemPreferenceChange);
    mediaQuery = null;
    doc.removeEventListener("click", handleClick);
    win.removeEventListener("pagehide", handlePageHide);
  }

  return Object.freeze({ dispose, snapshotTheme, start });
}

function hasThemePresentationBrowserContract(win, doc) {
  const root = doc?.documentElement;
  const body = doc?.body;
  return Boolean(
    win
    && root
    && typeof root.getAttribute === "function"
    && typeof root.setAttribute === "function"
    && typeof doc.addEventListener === "function"
    && typeof doc.removeEventListener === "function"
    && typeof doc.querySelectorAll === "function"
    && (!body || (
      typeof body.appendChild === "function"
      && typeof doc.createElement === "function"
      && typeof doc.getElementById === "function"
    ))
    && typeof win.addEventListener === "function"
    && typeof win.removeEventListener === "function"
    && typeof win.dispatchEvent === "function"
    && typeof win.CustomEvent === "function"
  );
}

if (hasThemePresentationBrowserContract(globalThis.window, globalThis.document)) {
  createThemePresentationController({
    repository: browserThemePreferenceRepository(),
  }).start();
}
